import React, { useState, useCallback, useRef } from "react";
import * as XLSX from "xlsx";
import { CircularProgress } from "@mui/material";
import AxiosInstance from "../../../utils/AxiosInstance";
import { useShowAlert } from "../../../context/AlertContext";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import { BarcodeIcon, DeleteIcon, DownloadIcon, UploadIcon, XIcon } from "../../../constants/Icons";

const requiredColumns = [
  "Title",
  "SKU Code",
  "HSN/SAC",
  "MRP",
  "Sale Price",
  "Tax Percentage",
  "Landing Cost",
  "Opening Stock",
  "Search Tag",
  "Brand",
  "Note",
];

/* ---------------- VALIDATION FUNCTION ---------------- */

const validateProductRow = (row, codeSet) => {
  const errors = {};
  const numberRegex = /^\d+(\.\d+)?$/;
  const integerRegex = /^\d+$/;

  if (!row["Title"]) {
    errors.title = "Title is required";
  }

  const code = String(row["SKU Code"] || "").trim();

  if (code) {
    if (codeSet.has(code)) {
      errors.code = "Product SKU Code must be unique";
    } else {
      codeSet.add(code);
    }
  }

  /* HSN VALIDATION */
  const hsn = String(row["HSN/SAC"] || "").trim();
  const hsnRegex = /^\d{4,8}$/;

  if (hsn && !hsnRegex.test(hsn)) {
    errors.hsnsac = "HSN/SAC must be 4 to 8 digits";
  }

  const mrp = row["MRP"];
  const salePrice = row["Sale Price"];

  // ❌ Both empty = Error
  if (!mrp && !salePrice) {
    errors.mrp = "MRP or Sale Price is required";
  }

  // If present, validate format
  if (mrp && !numberRegex.test(mrp)) {
    errors.mrp = "MRP must be a valid number";
  }

  if (salePrice && !numberRegex.test(salePrice)) {
    errors.saleprice = "Sale Price must be a valid number";
  }

  if (row["Tax Percentage"] && !numberRegex.test(row["Tax Percentage"])) {
    errors.taxpercentage = "Tax Percentage must be a number";
  }

  if (row["Landing Cost"] && !numberRegex.test(row["Landing Cost"])) {
    errors.landingcost = "Landing Cost must be a number";
  }

  /* ✅ PRODUCT TAG VALIDATION (OPTIONAL FIELD) */
  // const tag = String(row["Search Tag"] || "").trim();

  // if (tag && !tagRegex.test(tag)) {
  //   errors.productTag =
  //     "Search Tag must be alphanumeric (comma separated allowed)";
  // }

  // const tag = row["Search Tag"] ? String(row["Search Tag"]) : "";
  // if (tag === "") {
  //   // optional: allow empty OR ignore
  // }
  // Opening Stock validation (optional but must be integer)
  const openingStock = String(row["Opening Stock"] || "").trim();

  if (openingStock !== "") {
    const qty = Number(openingStock);

    if (!Number.isInteger(qty)) {
      errors.openingstock = "Opening Stock must be an integer";
    } else if (qty < 0) {
      errors.openingstock = "Opening Stock must be a positive number";
    }
  }

  return Object.keys(errors).length ? errors : null;
};

/* ---------------- COMPONENT ---------------- */

const ProductExcelUploader = ({ onDone }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [validRows, setValidRows] = useState([]);
  const [errorRows, setErrorRows] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const showAlert = useShowAlert();
  const [uploadModal, setUploadModal] = useState(false);
  const [uploadSummary, setUploadSummary] = useState(null);
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const fileInputRef = useRef(null);
  const [duplicateErrorModal, setDuplicateErrorModal] = useState(false);
  const [serverDuplicates, setServerDuplicates] = useState([]);
  const [missingCodeRows, setMissingCodeRows] = useState([]);
  const [showMissingCodeModal, setShowMissingCodeModal] = useState(false);
  const [unuploadedRows, setUnuploadedRows] = useState([]);
  const [showUnuploadedModal, setShowUnuploadedModal] = useState(false);
  const [uploadStats, setUploadStats] = useState({
    total: 0,
    processed: 0, //  NEW
    uploaded: 0,
    duplicate: 0,
    error: 0,
    unuploaded: 0, //  NEW
  });

  /* ---------------- SAMPLE DOWNLOAD ---------------- */
  const downloadSampleExcel = () => {
    const sampleData = [
      {
        Title: "Casual Sneakers",
        "SKU Code": "250818300",
        "HSN/SAC": "3333",
        MRP: "2000",
        "Sale Price": "1800",
        "Tax Percentage": "18",
        "Landing Cost": "1500",
        "Opening Stock": "10",
        "Search Tag": "Footwear Sneakers",
        Brand: "BR0123", // ✅ NEW
        Note: "Lightweight comfort",
      },
    ];

    const formattedData = sampleData.map((row) => {
      const fullRow = {};
      requiredColumns.forEach((col) => {
        fullRow[col] = row[col] || "";
      });
      return fullRow;
    });

    formattedData.push({});
    formattedData.push({});

    formattedData.push({
      Title: "===== VALIDATION RULES =====",
    });

    formattedData.push({
      "SKU Code": "SKU Code → Mandatory & must be unique",
    });

    formattedData.push({
      Title: "Title → Mandatory",
    });

    formattedData.push({
      MRP: "MRP → Mandatory & numeric",
    });

    formattedData.push({
      "Sale Price": "Sale Price → Mandatory & numeric",
    });

    formattedData.push({
      "Tax Percentage": "Tax Percentage → Optional numeric",
    });

    formattedData.push({
      "Landing Cost": "Landing Cost → Optional numeric",
    });

    const ws = XLSX.utils.json_to_sheet(formattedData, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product_Upload_Template");

    XLSX.writeFile(wb, "sample_product_upload.xlsx");
  };

  /* ---------------- FILE UPLOAD ---------------- */

  const toSafeString = (value) => {
    if (value === undefined || value === null) return "";
    return String(value).trim();
  };

  const toSafeNumber = (value) => {
    if (value === undefined || value === null || value === "") return 0;

    const num = Number(value);
    return isNaN(num) ? 0 : num;
  };

  const handleFileUpload = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    setErrors([]);
    setValidRows([]);
    setErrorRows([]);
    setMissingCodeRows([]);
    setUploadStats({
      total: 0,
      processed: 0,
      processing: 0,
      uploaded: 0,
      duplicate: 0,
      error: 0,
      unuploaded: 0,
    });

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, {
        type: "array",
        raw: false,
      });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      //  Normalize function (VERY IMPORTANT)
      const normalizeHeader = (value) => {
        return String(value || "")
          .replace(/\u00A0/g, " ") // remove non-breaking space
          .replace(/\s+/g, " ") // collapse multiple spaces
          .trim()
          .toLowerCase();
      };

      //  Read header row
      const headerRow = XLSX.utils.sheet_to_json(sheet, {
        header: 1,
        raw: false,
      })[0];

      if (!headerRow || headerRow.length === 0) {
        setErrors(["Excel file is empty"]);
        setLoading(false);
        return;
      }

      //  Normalize uploaded headers
      const normalizedUploadedHeaders = headerRow.map(normalizeHeader);

      //  Normalize required headers
      const normalizedRequiredHeaders = requiredColumns.map(normalizeHeader);

      //  Find missing
      const missingColumns = normalizedRequiredHeaders.filter(
        (col) => !normalizedUploadedHeaders.includes(col),
      );

      //  Find extra
      const extraColumns = normalizedUploadedHeaders.filter(
        (col) => col && !normalizedRequiredHeaders.includes(col),
      );

      if (missingColumns.length > 0) {
        setErrors([`Missing required column(s): ${missingColumns.join(", ")}`]);
        setLoading(false);
        return;
      }

      if (extraColumns.length > 0) {
        setErrors([`Invalid column(s) found: ${extraColumns.join(", ")}`]);
        setLoading(false);
        return;
      }

      //  Now read actual data
      const rows = XLSX.utils.sheet_to_json(sheet, {
        raw: false,
        defval: "",
      });

      const valid = [];
      const invalid = [];
      const missing = []; // ✅ NEW LOCAL ARRAY
      const codeSet = new Set();

      rows.forEach((row) => {
        const formattedRow = {};
        requiredColumns.forEach((col) => {
          formattedRow[col] = toSafeString(row[col]);
        });

        const rowError = validateProductRow(formattedRow, codeSet);

        if (rowError) {
          invalid.push({ ...formattedRow, __errors: rowError });
        } else {
          const rawMRP = formattedRow["MRP"];
          const rawSalePrice = formattedRow["Sale Price"];

          let finalMRP = toSafeNumber(rawMRP);
          let finalSalePrice = toSafeNumber(rawSalePrice);

          // ✅ If MRP empty but Sale Price exists → copy Sale Price
          if (!rawMRP && rawSalePrice) {
            finalMRP = finalSalePrice;
          }

          // ✅ If Sale Price empty → make 0
          if (!rawSalePrice || Number(rawSalePrice) === 0) {
            finalSalePrice = finalMRP;
          }

          // ✅ NEW VALIDATION (IMPORTANT)
          if (finalMRP < finalSalePrice) {
            invalid.push({
              ...formattedRow,
              __errors: {
                mrp: `MRP (${finalMRP}) cannot be less than Sale Price (${finalSalePrice})`,
                saleprice: `Sale Price (${finalSalePrice}) is greater than MRP (${finalMRP})`,
              },
            });
            return;
          }

          const productObj = {
            title: formattedRow["Title"],
            code: formattedRow["SKU Code"],
            hsnsac: formattedRow["HSN/SAC"],
            note: formattedRow["Note"],
            // searchTag: formattedRow["Product Tag"],
            mrp: finalMRP,
            saleprice: finalSalePrice,
            taxpercentage: toSafeNumber(formattedRow["Tax Percentage"]) || 0,
            landingcost: toSafeNumber(formattedRow["Landing Cost"]) || 0,
            // ✅ ADD THIS (optional)
            // brandId: toSafeString(formattedRow["Brand"]),
          };

          // ✅ Conditionally add fields
          const rawTag = row["Search Tag"];

          if (
            rawTag !== undefined &&
            rawTag !== null &&
            String(rawTag).trim() !== ""
          ) {
            productObj.searchTag = String(rawTag).trim();
          }

          const brand = toSafeString(formattedRow["Brand"]);
          if (brand) {
            productObj.brandId = brand;
          }
          const openingStock = Number(formattedRow["Opening Stock"]);

          if (!isNaN(openingStock) && openingStock > 0) {
            productObj.openingStock = openingStock;
          }
          if (!productObj.code) {
            missing.push(productObj); // ✅ PUSH TO LOCAL
          } else {
            valid.push(productObj);
          }
        }
      });

      setValidRows(valid);
      setErrorRows(invalid);
      setMissingCodeRows(missing); // ✅ CLEAN SET
    } catch (err) {
      console.error(err);
      setErrors(["Failed to process file"]);
    }

    setLoading(false);
  }, []);

  const generateBarcodesForMissing = async () => {
    if (!missingCodeRows.length) return;

    try {
      setLoading(true);

      const res = await AxiosInstance.post("module/product/bulk-barcode", {
        count: missingCodeRows.length,
      });

      const { prefix, startNumber } = res?.data?.results;

      let currentNumber = startNumber;

      const updatedRows = missingCodeRows.map((row) => {
        const generatedCode = `${prefix}${currentNumber++}`;
        return {
          ...row,
          code: generatedCode,
        };
      });

      // Merge into valid rows
      setValidRows((prev) => [...prev, ...updatedRows]);
      setMissingCodeRows([]);
      setShowMissingCodeModal(false);

      showAlert("success", "Barcodes generated successfully");
    } catch (err) {
      showAlert("error", "Failed to generate barcodes");
    } finally {
      setLoading(false);
    }
  };
  const chunkArray = (array, size) => {
    const result = [];
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size));
    }
    return result;
  };

  /* ---------------- SUBMIT ---------------- */

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const handleSubmit = async () => {
    if (!validRows.length) return;

    try {
      setLoading(true);
      setUploadModal(true);

      const BATCH_SIZE = 200;

      setUploadStats({
        total: validRows.length,
        processed: 0,
        processing: 0,
        uploaded: 0,
        duplicate: 0,
        error: 0,
        unuploaded: 0,
      });

      const transformedProducts = validRows.map((product) => ({
        ...product,
        mrp: CurrencyConverter.toSubunit(product.mrp, "INR"),
        saleprice: CurrencyConverter.toSubunit(product.saleprice, "INR"),
        landingcost: CurrencyConverter.toSubunit(
          product.landingcost || 0,
          "INR",
        ),
      }));

      const batches = chunkArray(transformedProducts, BATCH_SIZE);

      let duplicateAccumulator = [];
      let errorAccumulator = [];
      let unuploadedAccumulator = [];

      for (let i = 0; i < batches.length; i++) {
        const currentBatch = batches[i];

        setUploadStats((prev) => ({
          ...prev,
          processing: currentBatch.length,
        }));

        try {
          const res = await AxiosInstance.post("module/product/bulk", {
            products: currentBatch,
          });

          const insertedCount = res?.data?.results?.insertedProdutsCount || 0;

          setUploadStats((prev) => {
            const newUploaded = prev.uploaded + insertedCount;
            const newProcessed = prev.processed + currentBatch.length;

            return {
              ...prev,
              uploaded: newUploaded,
              processed: newProcessed,
              unuploaded: prev.total - newProcessed,
            };
          });
        } catch (err) {
          if (err?.response?.data?.code === "DUPLICATE_FOUND_IN_STORE") {
            const { duplicateNos = [] } = err?.response?.data?.results || {};

            const duplicateIndexes = duplicateNos.map((n) => Number(n) - 1);

            const duplicatesInBatch = [];
            const retryBatch = [];

            currentBatch.forEach((item, index) => {
              if (duplicateIndexes.includes(index)) {
                duplicatesInBatch.push(item);
              } else {
                retryBatch.push(item); // ✅ only valid remaining items
              }
            });

            // Accumulate duplicates
            duplicateAccumulator.push(...duplicatesInBatch);

            setUploadStats((prev) => ({
              ...prev,
              duplicate: prev.duplicate + duplicatesInBatch.length,
            }));

            //  If there are valid items remaining → retry upload
            if (retryBatch.length > 0) {
              try {
                const retryRes = await AxiosInstance.post(
                  "module/product/bulk",
                  {
                    products: retryBatch,
                  },
                );

                const insertedCount =
                  retryRes?.data?.results?.insertedProdutsCount || 0;

                setUploadStats((prev) => {
                  const newUploaded = prev.uploaded + insertedCount;
                  const newProcessed = prev.processed + currentBatch.length; // full batch processed

                  return {
                    ...prev,
                    uploaded: newUploaded,
                    processed: newProcessed,
                    unuploaded: prev.total - newProcessed,
                  };
                });
              } catch (retryErr) {
                // If retry fails, mark remaining as error
                errorAccumulator.push(...retryBatch);

                setUploadStats((prev) => ({
                  ...prev,
                  error: prev.error + retryBatch.length,
                  processed: prev.processed + currentBatch.length,
                }));
              }
            } else {
              // If entire batch duplicates
              setUploadStats((prev) => ({
                ...prev,
                processed: prev.processed + currentBatch.length,
              }));
            }
          } else {
            const serverErrors = err?.response?.data?.results?.products || {};

            if (Object.keys(serverErrors).length > 0) {
              Object.entries(serverErrors).forEach(([index, productErrors]) => {
                const product = currentBatch[Number(index)];

                const formattedErrors = {};

                Object.entries(productErrors).forEach(([field, value]) => {
                  if (field === "__errors") return;

                  if (value?.__errors?.length) {
                    formattedErrors[field] = value.__errors.join(", ");
                  }
                });

                errorAccumulator.push({
                  ...product,
                  __errors: formattedErrors,
                });
              });
            } else {
              errorAccumulator.push(
                ...currentBatch.map((item) => ({
                  ...item,
                  __errors: {
                    server: "Upload failed",
                  },
                })),
              );
            }

            setUploadStats((prev) => ({
              ...prev,
              error: prev.error + currentBatch.length,
              processed: prev.processed + currentBatch.length,
            }));
          }
        }
        if (i < batches.length - 1) {
          await delay(2000);
        }
      }

      if (unuploadedAccumulator.length > 0) {
        setUnuploadedRows(unuploadedAccumulator);
      }
      // ✅ Save duplicates to state
      if (duplicateAccumulator.length > 0) {
        setServerDuplicates(
          duplicateAccumulator.map((item, index) => ({
            index,
            data: item,
            fields: ["SKU Code"],
          })),
        );
      }

      // ✅ Save errors to errorRows

      if (errorAccumulator.length === 0 && duplicateAccumulator.length === 0) {
        showAlert("success", "Bulk upload completed successfully");
      } else if (
        errorAccumulator.length === 0 &&
        duplicateAccumulator.length > 0
      ) {
        showAlert(
          "warning",
          `Upload completed with ${duplicateAccumulator.length} duplicates skipped`,
        );
      } else if (errorAccumulator.length > 0) {
        showAlert(
          "error",
          `Upload completed with ${errorAccumulator.length} failed records`,
        );
      } else {
        showAlert("info", "Bulk upload process completed");
      }
    } catch (error) {
      showAlert("error", "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };

  const removeDuplicateRow = (indexToRemove) => {
    const updatedValidRows = validRows.filter(
      (_, index) => index !== indexToRemove,
    );

    const updatedServerDuplicates = serverDuplicates
      .filter((item) => item.index !== indexToRemove)
      .map((item) => {
        if (item.index > indexToRemove) {
          return { ...item, index: item.index - 1 };
        }
        return item;
      });

    setValidRows(updatedValidRows);
    setServerDuplicates(updatedServerDuplicates);

    if (updatedServerDuplicates.length === 0) {
      setDuplicateErrorModal(false);
    }
  };

  /* ---------------- DOWNLOAD ERRORS ---------------- */

  const downloadErrors = () => {
    if (!errorRows.length) return;

    const formattedErrors = errorRows.map(({ __errors, ...rest }) => {
      const fullRow = {};
      requiredColumns.forEach((col) => {
        fullRow[col] = rest[col] || "";
      });
      return fullRow;
    });

    const ws = XLSX.utils.json_to_sheet(formattedErrors, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product_Errors");

    XLSX.writeFile(wb, "product_error_rows.xlsx");
  };

  const downloadUnuploaded = () => {
    if (!unuploadedRows.length) return;

    const ws = XLSX.utils.json_to_sheet(unuploadedRows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Unuploaded");

    XLSX.writeFile(wb, "unuploaded_products.xlsx");
  };

  const downloadDuplicates = () => {
    if (!serverDuplicates.length) return;

    const formattedDuplicates = serverDuplicates.map((dup) => {
      const row = dup.data || {};

      return {
        Title: row.title || "",
        "SKU Code": row.code || "",
        "HSN/SAC": row.hsnsac || "",
        MRP: CurrencyConverter.toCurrency(row.mrp || 0, "INR"),
        "Sale Price": CurrencyConverter.toCurrency(row.saleprice || 0, "INR"),
        "Tax Percentage": row.taxpercentage || "",
        "Landing Cost": row.landingcost || "",
        "Opening Stock": row.openingStock || "",
        "Search Tag": row?.searchTag || "",
        Brand: row?.brandId || "",
        Note: row.note || "",
      };
    });

    const ws = XLSX.utils.json_to_sheet(formattedDuplicates, {
      header: requiredColumns,
    });

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Duplicate_Products");

    XLSX.writeFile(wb, "duplicate_products.xlsx");
  };

  const removeAllDuplicates = () => {
    if (!serverDuplicates.length) return;

    const duplicateIndexes = serverDuplicates.map((d) => d.index);

    const updatedValidRows = validRows.filter(
      (_, index) => !duplicateIndexes.includes(index),
    );

    setValidRows(updatedValidRows);
    setServerDuplicates([]);
    setDuplicateErrorModal(false);
  };

  const totalProducts =
    validRows.length + errorRows.length + missingCodeRows.length;

  const resetAll = () => {
    setValidRows([]);
    setErrorRows([]);
    setMissingCodeRows([]);
    setUploadCompleted(false);
    setUploadSummary(null);
    setShowErrorModal(false);
    setShowMissingCodeModal(false);
    setUploadStats({
      total: 0,
      processed: 0,
      processing: 0,
      uploaded: 0,
      duplicate: 0,
      error: 0,
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onDone?.();
  };

  const clearAll = () => {
    setValidRows([]);
    setErrorRows([]);
    setMissingCodeRows([]);
    setUploadCompleted(false);
    setUploadSummary(null);
    setShowErrorModal(false);
    setShowMissingCodeModal(false);
    setUploadStats({
      total: 0,
      processed: 0,
      processing: 0,
      uploaded: 0,
      duplicate: 0,
      error: 0,
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="card">
          <div className="card-body">
            <h4>Bulk Product Upload</h4>

            <label className="form-label mt-2">
              Upload Excel File{" "}
              <small
                style={{ cursor: "pointer", color: "blue" }}
                onClick={downloadSampleExcel}
              >
                (Download Sample)
              </small>
            </label>

            <div className="col-lg-4">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
                className="form-control"
              />
            </div>

            {/* SUMMARY SECTION */}
            {(validRows.length > 0 ||
              errorRows.length > 0 ||
              missingCodeRows.length > 0) && (
              <div className="mt-3 p-3 border rounded bg-light d-flex  align-items-center flex-wrap">
                {/* LEFT SIDE - COUNTS */}
                <div>
                  <span className="me-3 fw-bold">
                    Total: {totalProducts} Products
                  </span>

                  <span className="me-3 text-success fw-bold">
                    Valid Products: {validRows.length}
                  </span>
                </div>

                {/* RIGHT SIDE - ACTION BUTTONS */}
                <div className="d-flex gap-2 mt-2 mt-md-0 align-items-center">
                  {validRows.length > 0 && (
                    <>
                      {loading ? (
                        <button className="btn btn-sm btn-primary" disabled>
                          <CircularProgress size={22} className="text-white" />
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleSubmit}
                          disabled={loading || uploadCompleted}
                        >
                          <UploadIcon/> Upload Valid
                        </button>
                      )}
                    </>
                  )}

                  {missingCodeRows.length > 0 && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowMissingCodeModal(true)}
                    >
                      <BarcodeIcon iClass={"me-1"}/> Missing Code (
                      {missingCodeRows.length})
                    </button>
                  )}

                  {uploadStats.duplicate > 0 && (
                    <button
                      className="btn btn-danger btn-sm me-2"
                      onClick={() => {
                        setUploadModal(false);
                        setDuplicateErrorModal(true);
                      }}
                    >
                      View Duplicates ({uploadStats.duplicate})
                    </button>
                  )}

                  {errorRows.length > 0 && (
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => setShowErrorModal(true)}
                    >
                      View Errors ({errorRows.length})
                    </button>
                  )}
                </div>
                <div className="ms-auto">
                  <button
                    className="btn btn-outline-secondary btn-sm text-right"
                    onClick={clearAll}
                  >
                    <XIcon/> Clear
                  </button>
                </div>
              </div>
            )}

            {/* {loading && <CircularProgress size={25} className="mt-2" />} */}

            {errors.length > 0 && (
              <div className="text-danger mt-2">
                {errors.map((err, i) => (
                  <div key={i}>{err}</div>
                ))}
              </div>
            )}

            {validRows.length > 0 && (
              <div className="mt-3">
                <h6 className="text-gray">
                  Valid Products ({validRows.length})
                </h6>
                <div className="row">
                  <div className="col-lg-12 table-responsive rtable ">
                    <table className="table table-sm table-bordered mb-0 rtable">
                      <thead className="bg-light">
                        <tr>
                          {requiredColumns.map((col, i) => (
                            <th key={i}>{col === "Code" ? "SKU Code" : col}</th>
                          ))}
                        </tr>
                      </thead>

                      <tbody>
                        {validRows.map((row, i) => (
                          <tr key={i}>
                            <td>{row.title}</td>
                            <td>{row.code}</td>
                            <td>{row.hsnsac}</td>
                            <td>{row.mrp}</td>
                            <td>{row.saleprice}</td>
                            <td>{row.taxpercentage}</td>
                            <td>{row.landingcost}</td>
                            <td>{row.openingStock}</td>
                            <td>{row.searchTag}</td>
                            <td>{row.brandId}</td>
                            <td>{row.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {uploadModal && (
          <>
            <div className="modal-backdrop show custom-backdrop"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog  modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                  {/* HEADER */}
                  <div className="modal-header border-0">
                    <h5 className="modal-title fw-bold">Bulk Upload Status</h5>
                    <button
                      className="btn-close"
                      onClick={() => setUploadModal(false)}
                    ></button>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    {/* Total */}
                    {/* <div className="text-center mb-4">
                      <h6 className="text-muted mb-1">Total Records</h6>
                      <h2 className="fw-bold">{uploadStats.total}</h2>
                    </div> */}

                    {/* Stats Grid */}
                    <div className="row text-center g-3 mb-4">
                      <div className="col-6">
                        <div className="stat-card total">
                          <h6 className="text-muted mb-1">Total Records</h6>
                          <h4 className="fw-bold">{uploadStats.total}</h4>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          className="stat-card secondary clickable-card"
                          onClick={() => {
                            if (uploadStats.unuploaded > 0) {
                              setShowUnuploadedModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.unuploaded > 0
                                ? "pointer"
                                : "default",
                          }}
                        >
                          <h6>Unuploaded</h6>
                          <h4>{uploadStats.unuploaded}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="stat-card processing">
                          <h6>Processed</h6>
                          <h4>{uploadStats.processed}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div className="stat-card success">
                          <h6>Uploaded</h6>
                          <h4>{uploadStats.uploaded}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div
                          className="stat-card warning clickable-card"
                          onClick={() => {
                            if (uploadStats.duplicate > 0) {
                              setDuplicateErrorModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.duplicate > 0 ? "pointer" : "default",
                          }}
                        >
                          <h6>View Duplicates</h6>
                          <h4>{uploadStats.duplicate}</h4>
                        </div>
                      </div>

                      <div className="col-6">
                        <div
                          className="stat-card danger clickable-card"
                          onClick={() => {
                            if (uploadStats.error > 0) {
                              setShowErrorModal(true);
                            }
                          }}
                          style={{
                            cursor:
                              uploadStats.error > 0 ? "pointer" : "default",
                          }}
                        >
                          <h6>View Errors</h6>
                          <h4>{uploadStats.error}</h4>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="progress modern-progress">
                      <div
                        className={`progress-bar ${
                          uploadStats.uploaded + uploadStats.processing <
                          uploadStats.total
                            ? "progress-bar-striped progress-bar-animated"
                            : ""
                        }`}
                        role="progressbar"
                        style={{
                          width: `${
                            uploadStats.total
                              ? (uploadStats.processed / uploadStats.total) *
                                100
                              : 0
                          }%`,
                        }}
                      >
                        {uploadStats.total
                          ? Math.round(
                              (uploadStats.processed / uploadStats.total) * 100,
                            )
                          : 0}
                        %
                      </div>
                    </div>

                    {/* Completed Badge */}
                    {uploadStats.processed >= uploadStats.total && (
                      <div className="text-center mt-3 fw-semibold">
                        {uploadStats.uploaded === uploadStats.total ? (
                          <span className="text-success">
                            Upload Completed Successfully
                          </span>
                        ) : (
                          <span className="text-warning">
                            Upload Finished (Some Duplicates or Errors Found)
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer border-0 d-flex ">
                    {/* <div>
                      {uploadStats.duplicate > 0 && (
                        <button
                          className="btn btn-label-danger btn-sm me-2"
                          onClick={() => setDuplicateErrorModal(true)}
                        >
                          View Duplicates ({uploadStats.duplicate})
                        </button>
                      )}

                      {uploadStats.error > 0 && (
                        <button
                          className="btn  btn-label-danger btn-sm"
                          onClick={() => setShowErrorModal(true)}
                        >
                          View Errors ({uploadStats.error})
                        </button>
                      )}
                    </div> */}

                    <button
                      className="btn btn-primary btn-sm text-right"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                      disabled={loading}
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {showMissingCodeModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                  {/* HEADER */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h6 className="modal-title mb-0 ">
                      Missing Barcode Products ({missingCodeRows.length})
                    </h6>

                    <div>
                      {loading ? (
                        <button className="btn btn-primary btn-sm" disabled>
                          <CircularProgress size={18} />
                        </button>
                      ) : (
                        <button
                          className="btn btn-primary btn-sm me-2"
                          onClick={generateBarcodesForMissing}
                          disabled={loading}
                        >
                          <BarcodeIcon iClass={"me-1"}/>{" "}
                          <>Generate Barcodes</>
                        </button>
                      )}

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowMissingCodeModal(false)}
                      ></button>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    <div className="table-responsive">
                      <table className="table table-bordered table-sm">
                        <thead className="bg-light">
                          <tr>
                            <th>Title</th>
                            <th>SKU Code</th>
                            <th>HSN/SAC</th>
                            <th>MRP</th>
                            <th>Sale Price</th>
                            <th>Tax %</th>
                            <th>Landing Cost</th>
                            <th>Opening Stock</th>
                            <th>Search Tag</th>
                            <th>Brand</th>
                            <th>Note</th>
                          </tr>
                        </thead>

                        <tbody>
                          {missingCodeRows.map((row, i) => (
                            <tr key={i}>
                              <td>{row.title}</td>
                              <td>{row.code}</td>
                              <td>{row.hsnsac}</td>
                              <td>{row.mrp}</td>
                              <td>{row.saleprice}</td>
                              <td>{row.taxpercentage}</td>
                              <td>{row.landingcost}</td>
                              <td>{row.openingStock}</td>
                              <td>{row.searchTag}</td>
                              <td>{row.brandId}</td>
                              <td>{row.note}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* FOOTER */}
                  <div className="modal-footer d-flex justify-content-between">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowMissingCodeModal(false)}
                    >
                      <XIcon/> Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ERROR MODAL */}
        {showErrorModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div
              className="modal show d-block"
              id="CustomerModal"
              tabIndex="0"
              aria-modal="true"
              role="dialog"
            >
              <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content">
                  {/* HEADER */}
                  <div className="modal-header d-flex justify-content-between align-items-center">
                    <h6 className="modal-title  mb-0">
                      Error Products ({errorRows.length})
                    </h6>

                    <div>
                      <button
                        className="btn btn-export btn-sm me-2"
                        onClick={downloadErrors}
                      >
                        <DownloadIcon iClass={"fs-15"}/> Download as
                        Excel
                      </button>

                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowErrorModal(false)}
                      ></button>
                    </div>
                  </div>

                  {/* BODY */}
                  <div className="modal-body">
                    <div className="col-lg-12 table-responsive rtable">
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="bg-light">
                          <tr>
                            {/* ERROR COLUMN FIRST */}
                            <th>Error</th>
                            {requiredColumns.map((col, i) => (
                              <th key={i}>{col}</th>
                            ))}
                          </tr>
                        </thead>

                        <tbody>
                          {errorRows.map((row, i) => (
                            <tr key={i}>
                              {/* ERROR COLUMN FIRST */}
                              <td>
                                {Object.values(row.__errors).map((msg, idx) => (
                                  <div key={idx}>• {msg}</div>
                                ))}
                              </td>

                              {/* DATA COLUMNS */}
                              {requiredColumns.map((col, j) => {
                                const hasError =
                                  (row.__errors &&
                                    ((col === "Title" && row.__errors.title) ||
                                      (col === "SKU Code" &&
                                        row.__errors.code) ||
                                      (col === "HSN/SAC" &&
                                        row.__errors.hsnsac) ||
                                      (col === "MRP" && row.__errors.mrp) ||
                                      (col === "Sale Price" &&
                                        row.__errors.saleprice) ||
                                      (col === "Tax Percentage" &&
                                        row.__errors.taxpercentage) ||
                                      (col === "Landing Cost" &&
                                        row.__errors.landingcost))) ||
                                  (col === "Opening Stock" &&
                                    row.__errors.openingstock);
                                return (
                                  <td
                                    key={j}
                                    style={
                                      hasError
                                        ? {
                                            backgroundColor: "#ffe5e5",
                                            color: "#d8000c",
                                            fontWeight: "500",
                                          }
                                        : {}
                                    }
                                  >
                                    {row[col]}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* {uploadModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog  modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Successfully Uploaded</h5>
                    <button
                      className="btn-close"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3 text-center text-success">
                      <strong className="text-success">
                        Successfully Uploaded Counts:
                      </strong>{" "}
                      {uploadSummary?.uploaded}
                    </div>
                    {/* SHOW ERROR BUTTON 
                  </div>
                  <div className="modal-footer d-block">
                    {errorRows.length > 0 && (
                      <button
                        className="btn btn-danger btn-sm text-left"
                        onClick={() => {
                          setTimeout(() => {
                            setShowErrorModal(true);
                          }, 200);
                        }}
                      >
                        View Errors ({errorRows.length})
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm text-right"
                      onClick={() => {
                        setUploadModal(false);
                        resetAll();
                      }}
                    >
                      <i className="bx bx-check"></i> Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )} */}

        {showUnuploadedModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-danger">
                      Unuploaded Products ({unuploadedRows.length})
                    </h5>

                    <div>
                      <button
                        className="btn btn-sm btn-export me-2"
                        onClick={downloadUnuploaded}
                      >
                        <DownloadIcon/> Download as Excel
                      </button>

                      <button
                        className="btn-close"
                        onClick={() => setShowUnuploadedModal(false)}
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Title</th>
                          <th>SKU Code</th>
                          <th>HSN/SAC</th>
                          <th>MRP</th>
                          <th>Sale Price</th>
                          <th>Tax %</th>
                          <th>Landing Cost</th>
                          <th>Opening Stock</th>
                          <th>Search Tag</th>
                          <th>Brand</th>
                          <th>Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unuploadedRows.map((row, i) => (
                          <tr key={i}>
                            <td>{row.title}</td>
                            <td>{row.code}</td>
                            <td>{row.hsnsac}</td>
                            <td>{row.mrp}</td>
                            <td>{row.saleprice}</td>
                            <td>{row.taxpercentage}</td>
                            <td>{row.landingcost}</td>
                            <td>{row.openingStock}</td>
                            <td>{row.searchTag}</td>
                            <td>{row.brandId}</td>
                            <td>{row.note}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="modal-footer">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setShowUnuploadedModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {duplicateErrorModal && (
          <div>
            <div className="modal-backdrop show"></div>

            <div className="modal show d-block" tabIndex="0">
              <div className="modal-dialog modal-xl  modal-dialog-scrollable">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title text-danger">
                      Duplicate Products ({serverDuplicates.length})
                    </h5>

                    <div>
                      <button
                        className="btn btn-sm btn-export me-2"
                        onClick={downloadDuplicates}
                      >
                        <DownloadIcon/> Download as Excel
                      </button>

                      <button
                        className="btn-close"
                        onClick={() => setDuplicateErrorModal(false)}
                      ></button>
                    </div>
                  </div>

                  <div className="modal-body">
                    {/* <div className="alert alert-danger">
                      {serverDuplicates[0]?.message}
                    </div> */}

                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr>
                          <th>Error</th>
                          <th>Title</th>
                          <th>SKU Code</th>
                          <th>HSN/SAC</th>
                          <th>MRP</th>
                          <th>Sale Price</th>
                          <th>Opening Stock</th>
                          <th>Search Tag</th>
                          <th>Brand</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {serverDuplicates.map((dup, i) => (
                          <tr key={i}>
                            <td>
                              {dup.fields.map((field, idx) => (
                                <div key={idx}>{field} already exists</div>
                              ))}
                            </td>

                            <td>{dup.data?.title}</td>

                            <td
                              style={{
                                backgroundColor: "#ffe5e5",
                                color: "#d8000c",
                                fontWeight: "500",
                              }}
                            >
                              {dup.data?.code}
                            </td>

                            <td>{dup.data?.hsnsac}</td>
                            <td>
                              {CurrencyConverter.toCurrency(
                                dup.data?.mrp || 0,
                                "INR",
                              )}
                            </td>
                            <td>
                              {CurrencyConverter.toCurrency(
                                dup.data?.saleprice || 0,
                                "INR",
                              )}
                            </td>
                            <td>{dup.data?.openingStock}</td>
                            <td>{dup.data?.searchTag}</td>
                            <td>{dup.data?.brandId}</td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-label-danger shadow-none"
                                onClick={() => removeDuplicateRow(dup.index)}
                              >
                                <DeleteIcon/>
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="modal-footer d-flex justify-content-between">
                    <button
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => setDuplicateErrorModal(false)}
                    >
                      <XIcon/> Close
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={removeAllDuplicates}
                    >
                      <XIcon/> Remove All Duplicates
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductExcelUploader;
